import React, { useState, useRef, useEffect } from 'react';
import './PiPVideoFeed.css';

/**
 * PiPVideoFeed - Draggable Picture-in-Picture video for the local user
 * Used when the sidebar is collapsed
 */
const PiPVideoFeed = ({
    videoRef,
    currentUser,
    micOn,
    camOn,
    onMicToggle,
    onCamToggle,
    onExpand, // Callback to expand sidebar/restore view
    visible = true,
}) => {
    const [position, setPosition] = useState({ x: window.innerWidth - 260 - 24, y: 80 });
    const [isDragging, setIsDragging] = useState(false);
    const dragRef = useRef({ startX: 0, startY: 0, startLeft: 0, startTop: 0 });
    const pipRef = useRef(null);

    const handleMouseDown = (e) => {
        // Don't drag if clicking controls
        if (e.target.closest('button')) return;

        setIsDragging(true);
        dragRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            startLeft: position.x,
            startTop: position.y
        };
    };

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isDragging) return;

            const dx = e.clientX - dragRef.current.startX;
            const dy = e.clientY - dragRef.current.startY;

            // Calculate new position with bounds checking based on 260x195 size
            const margin = 12;
            const maxX = Math.max(margin, window.innerWidth - 260 - margin);
            const maxY = Math.max(70, window.innerHeight - 195 - margin);
            const newX = Math.max(margin, Math.min(maxX, dragRef.current.startLeft + dx));
            const newY = Math.max(70, Math.min(maxY, dragRef.current.startTop + dy));

            setPosition({ x: newX, y: newY });
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging]);

    return (
        <div
            className={`pip-video-feed ${isDragging ? 'dragging' : ''}`}
            style={{
                left: position.x,
                top: position.y,
                display: visible ? 'block' : 'none',
            }}
            ref={pipRef}
            onMouseDown={handleMouseDown}
        >
            <div className="pip-video-container">
                <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="pip-video"
                />
                <div className="pip-overlay">
                    <span className="pip-label">{currentUser || 'Me'}</span>
                    <div className="pip-controls">
                        <button
                            className={`pip-btn ${micOn ? '' : 'off'}`}
                            onClick={onMicToggle}
                            title={micOn ? "Mute" : "Unmute"}
                        >
                            {micOn ? '🎤' : '🔇'}
                        </button>
                        <button
                            className={`pip-btn ${camOn ? '' : 'off'}`}
                            onClick={onCamToggle}
                            title={camOn ? "Turn off camera" : "Turn on camera"}
                        >
                            {camOn ? '📷' : '📷'}
                        </button>
                        <button
                            className="pip-btn expand"
                            onClick={onExpand}
                            title="Expand Sidebar"
                        >
                            ⤢
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PiPVideoFeed;
